/**
 * Which material a court shows (speck-opencourt/entities/Court.md): every item whose coverage is the
 * court's own node in the jurisdiction tree (jurisdictions.json) or a node above it, or one of the court's
 * regions or a node above that, and whose applies_to_levels is empty or includes the court's level. A form
 * takes its issuer's coverage, and both the issuer's and the form's own applies_to_levels have to let the
 * court through.
 */
export function coverage(jurisdictions) {
  const parent = new Map(jurisdictions.map((j) => [j.id, j.parent || null]));
  // The node, then each node above it up to the root. scripts/build-content.py checks the tree has no cycles.
  const ancestors = (node) => {
    const out = [];
    for (let n = node; n; n = parent.get(n)) out.push(n);
    return out;
  };
  // Every node whose material reaches a court, nearest first: its own node and those above it, and each of its
  // regions (the Texas counties a district court serves) and those above it, deeper nodes before the root.
  const depth = (node) => ancestors(node).length;
  const reach = (court) => [...new Set([court.coverage_node, ...(court.regions || [])].flatMap(ancestors))]
    .sort((a, b) => depth(b) - depth(a));
  const levelOk = (court, levels) => !levels || !levels.length || levels.includes(court.level);
  const covers = (court, node, levels) => reach(court).includes(node) && levelOk(court, levels);
  const formCovers = (court, form, issuer) =>
    covers(court, issuer.coverage, issuer.applies_to_levels) && levelOk(court, form.applies_to_levels);
  // Where a court files a form: its own listings, else those of the nearest node above it that has any
  // (the Judicial Council's category, listed for "ca"), its regions coming before the system root.
  const divisions = (form, court) => {
    for (const node of reach(court)) {
      const own = [...new Set(form.listings.filter((l) => l.court === node).map((l) => l.division))];
      if (own.length) return own;
    }
    return [];
  };
  return { ancestors, reach, covers, formCovers, divisions };
}
